import { useEffect, useRef, useState } from 'react';

import {
  PHASE_LABELS,
  targetPercent,
  type GenerationPhase,
} from '../../model/generationProgress';

const TICK_MS = 200;
const EASE_MS = 400;

export interface GenerationProgressView {
  percent: number;
  label: string;
  failed: boolean;
}

/**
 * Smooth the server's phase into a percent that only moves forward.
 *
 * A new stage eases in over about 400ms instead of jumping. Between polls the
 * bar creeps using the time since the phase arrived, on top of the elapsed
 * time the server already reported.
 */
export function useGenerationProgress({
  status,
  phase,
}: {
  status: string;
  phase: GenerationPhase | null;
}): GenerationProgressView {
  const failed = status === 'GENERATION_FAILED';
  const completed = status === 'GENERATION_COMPLETED';
  const [percent, setPercent] = useState(() =>
    completed ? 100 : targetPercent(phase, 0)
  );
  const shownRef = useRef(percent);
  const arrivedAtRef = useRef(Date.now());
  const frozenRef = useRef(false);

  useEffect(() => {
    arrivedAtRef.current = Date.now();
  }, [phase]);

  useEffect(() => {
    if (failed) {
      frozenRef.current = true;
      return;
    }
    frozenRef.current = false;

    let lastTick = Date.now();
    const tick = () => {
      if (frozenRef.current) return;
      const now = Date.now();
      const dt = now - lastTick;
      lastTick = now;
      const extra = now - arrivedAtRef.current;
      const target = completed ? 100 : targetPercent(phase, extra);
      const shown = shownRef.current;
      const next =
        target <= shown
          ? shown
          : shown + (target - shown) * Math.min(1, dt / EASE_MS);
      shownRef.current = next;
      setPercent(next);
    };

    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [phase, failed, completed]);

  const label =
    phase && PHASE_LABELS[phase.phase]
      ? PHASE_LABELS[phase.phase]
      : 'Generating fingerings...';

  return {
    percent: completed ? Math.max(percent, shownRef.current) : percent,
    label,
    failed,
  };
}
